/**
 * StemConsole — a multitrack audition console for stems that already live in
 * memory as AudioBuffers.
 *
 * WHY IT EXISTS (measured against the category, 2026-08-18)
 * ────────────────────────────────────────────────────────
 * Every serious separator — Moises, LALAL.AI, MVSEP, DrumSplit — hands the
 * result back as a CONSOLE: all stems on one clock, solo/mute/volume, a
 * waveform per stem, one transport. /drum-separator handed it back as four
 * independent players that could only sound ONE part at a time, so the single
 * question the tool exists to answer — «does the kick come out clean UNDER the
 * rest of the kit?» — could not be asked at all. The parts also sum back to
 * the input exactly, which means the console doubles as the «drums minus one
 * part» export that the paid competitors sell as a separate product: whatever
 * you hear is what `gains()` renders.
 *
 * Not the same thing as /stem-splitter's player (stem-split-paid.js): there the
 * stems arrive as URLs and are streamed through <audio> + MediaElementSource,
 * because four decoded stems of a 10-minute song are ~850 MB of Float32. Here
 * the buffers are ALREADY decoded — the tool produced them — so the cheap and
 * sample-exact path is four BufferSources started at one `currentTime`. Sharing
 * one implementation between the two would mean carrying the streaming machinery
 * (drift correction, readyState races) for a case that cannot drift.
 *
 * This module emits documented class names and no CSS. The sheet is
 * public/css/stem-console.css (moved out of /drum-separator's blade 2026-09-19,
 * when /drum-remover became the second consumer); a page links it itself.
 *
 * API
 * ───
 *   var c = window.StemConsole.create(rootEl, {
 *       tracks: [{ key:'kick', label:'Бочка', color:'#f59e0b', buffer: AudioBuffer }, …],
 *       t: { play, pause, solo, mute, solo_full, mute_full, volume_for,
 *            loop, seek, empty, download },
 *       onDownload: function(track){},          // per-row ↓ (omit → no ↓ column)
 *       onPlayState: function(playing){}         // optional
 *   });
 *
 *   c.slot                         // empty <div> above the transport for the
 *                                  // consumer's own control (e.g. a variant switch)
 *   c.setBuffers({kick: buf, …})   // swap variant in place, keeps position + gains
 *   c.play(sec) · c.pause() · c.seek(sec) · c.stop()
 *   c.position() · c.duration() · c.playing()
 *   c.gains()                      // {key: 0..1} with solo/mute/volume resolved
 *   c.isDefaultMix()               // true when nothing is muted/soloed/attenuated
 *   c.setMutes({key: true, …})     // a preset: these rows muted, the rest on, solos released
 *   c.relayout()                   // redraw waveforms (resize / theme change)
 *   c.destroy()
 */
(function () {
'use strict';

if (window.StemConsole) { return; }

var SVG_PLAY  = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
var SVG_PAUSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 5h4v14H6zm8 0h4v14h-4z" fill="currentColor"/></svg>';
var SVG_DL    = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function fmt(t) {
    t = Math.max(0, Math.floor(t || 0));
    return Math.floor(t / 60) + ':' + ('0' + (t % 60)).slice(-2);
}

function ctxOf() {
    var C = window.AudioContext || window.webkitAudioContext;
    if (!C) { return null; }
    return window.__sharedAudioCtx || (window.__sharedAudioCtx = new C());
}

// Level of a buffer on a strided scan — RMS, not peak, and the difference is
// not academic: fed a groove with NO snare in it, the snare model still returns
// a part peaking at -25 dB (one bleed transient) while its RMS sits at -41 dB.
// Peak says «there is something here», RMS says «there is nothing here», and
// the second one is what a listener hears. Measured 2026-08-18 on a synthetic
// kick+hi-hat loop.
function levelOf(buf) {
    var sum = 0, n = 0;
    for (var c = 0; c < buf.numberOfChannels; c++) {
        var d = buf.getChannelData(c);
        for (var i = 0; i < d.length; i += 16) { sum += d[i] * d[i]; n++; }
    }
    return n ? Math.sqrt(sum / n) : 0;
}

/* A drum kit is a set of RELATIVE levels: a track with no toms still has a toms
 * part, carrying bleed. So the badge fires on true silence, OR on a part far
 * enough under the loudest one to be inaudible inside the kit. The wording it
 * drives says «almost empty», not «silent», because that is exactly what it is:
 * nearly nothing, not nothing.
 *
 * ⚠️⚠️ isEmpty ЭКСПОРТИРУЕТСЯ, и это не удобство, а ремонт. Ровно то же решение
 * было продублировано в туле (gateHits в drum-separator.blade.php, гейт
 * фантомных ударов в MIDI), копии разъехались, и после переноса порога badge
 * говорил «почти пусто», пока рядом считался 51 удар несуществующего снейра.
 * Одно решение — одно место. */
var REL = 0.0316;                       // -30 дБ, см. замер выше
function markEmpty(tracks) {
    var loudest = 0;
    tracks.forEach(function (t) { if (t.level > loudest) { loudest = t.level; } });
    // Порог ЗАМЕРЕН, а не выбран на глаз (2026-08-21, 60 фрагментов эталона +
    // браузерный прогон грува без снейра):
    //   настоящие части          — выше -25 дБ у 239 из 240;
    //   утечка в отсутствующую   — -38…-44 дБ.
    // Между ними 13 дБ пустого зазора. Прежние -40 дБ стояли ВПЛОТНУЮ к утечке,
    // и на тихой записи она вылезала на -38 — badge гас, а в MIDI уезжал 51
    // фантомный удар. -30 дБ ловит утечку и не даёт НИ ОДНОЙ новой ложной
    // пометки: ниже -30 на всём наборе лежит ровно та же единственная часть, что
    // и ниже -40 (томы -46.9 дБ, там и правда почти тишина).
    // ⚠️ Абсолютный порог — 1e-6, а НЕ 1e-4: у по-настоящему тихой записи под
    // 1e-4 уходили ВСЕ части разом, и кит целиком объявлялся пустым. Абсолютный
    // порог отвечает только на вопрос «файл вообще молчит», а «эта часть тихая
    // относительно остальных» — работа относительного.
    tracks.forEach(function (t) { t.empty = isEmpty(t.level, loudest); });
}
function isEmpty(level, loudest) {
    return level < 1e-6 || (loudest > 0 && level < loudest * REL);
}

/* Peak-preserving decimation for the waveform. WaveformPreview walks EVERY
 * sample of every channel; on a 15-minute stereo track that is ~80 M reads per
 * part and four parts freeze the page right after the split — and again on
 * every resize. One pass builds a mono envelope of at most ~2 M points that the
 * renderer (and any redraw) then works on; taking the extreme of each group,
 * not the first sample, keeps transients — which on a drum track IS the shape.
 * The shim is what AudioPost.makeBuf produces: WaveformPreview only ever reads
 * numberOfChannels / length / duration / getChannelData. */
var WAVE_MAX = 1 << 21;
function waveSource(buf) {
    if (buf.length <= WAVE_MAX) { return buf; }
    var step = Math.ceil(buf.length / WAVE_MAX);
    var out = new Float32Array(Math.ceil(buf.length / step));
    var chans = [];
    for (var c = 0; c < buf.numberOfChannels; c++) { chans.push(buf.getChannelData(c)); }
    for (var i = 0, o = 0; i < buf.length; i += step, o++) {
        var end = Math.min(buf.length, i + step), best = 0, bestAbs = 0;
        for (var j = i; j < end; j++) {
            for (var k = 0; k < chans.length; k++) {
                var v = chans[k][j], a = v < 0 ? -v : v;
                if (a > bestAbs) { bestAbs = a; best = v; }
            }
        }
        out[o] = best;
    }
    return {
        numberOfChannels: 1, length: out.length, duration: buf.duration,
        sampleRate: buf.sampleRate / step,
        getChannelData: function () { return out; }
    };
}

function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) { n.className = cls; }
    if (html != null) { n.innerHTML = html; }
    return n;
}

function create(root, opts) {
    opts = opts || {};
    var T = opts.t || {};
    var tracks = (opts.tracks || []).map(function (s) {
        return { key: s.key, label: s.label, color: s.color || 'currentColor', buffer: s.buffer,
                 solo: false, mute: false, vol: 1, level: levelOf(s.buffer), empty: false,
                 el: {}, node: null, gain: null, wave: null };
    });
    if (!tracks.length) { return null; }
    markEmpty(tracks);

    var loop = false, playing = false, raf = 0, startedAt = 0, offset = 0, token = 0, destroyed = false;

    function duration() {
        var d = 0;
        for (var i = 0; i < tracks.length; i++) { if (tracks[i].buffer.duration > d) { d = tracks[i].buffer.duration; } }
        return d;
    }

    /* ── DOM ──────────────────────────────────────────────────────────────── */
    root.classList.add('sc');
    // no per-row ↓ (the consumer hands files out through its own buttons): the grid drops the
    // column instead of keeping a 40 px hole at the end of every row
    root.classList.toggle('sc--nodl', typeof opts.onDownload !== 'function');
    root.innerHTML = '';

    var bar = el('div', 'sc-transport');
    var playBtn = el('button', 'sc-play', SVG_PLAY);
    playBtn.type = 'button';
    playBtn.setAttribute('aria-label', T.play || 'Play');
    var cur = el('span', 'sc-t sc-t-cur', '0:00');
    var seek = document.createElement('input');
    seek.type = 'range'; seek.className = 'sc-seek';
    seek.min = '0'; seek.max = '1000'; seek.value = '0'; seek.step = '1';
    seek.setAttribute('aria-label', T.seek || 'Position');
    var tot = el('span', 'sc-t sc-t-tot', fmt(duration()));
    var loopBtn = el('button', 'sc-loop', '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path d="M17 2l4 4-4 4M7 22l-4-4 4-4M21 6H8a5 5 0 00-5 5v1M3 18h13a5 5 0 005-5v-1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    loopBtn.type = 'button';
    loopBtn.setAttribute('aria-pressed', 'false');
    loopBtn.setAttribute('aria-label', T.loop || 'Loop');
    if (T.loop) { loopBtn.title = T.loop; }
    bar.appendChild(playBtn); bar.appendChild(cur); bar.appendChild(seek); bar.appendChild(tot); bar.appendChild(loopBtn);

    var slotBefore = el('div', 'sc-slot sc-slot-top');
    var list = el('div', 'sc-tracks');

    root.appendChild(slotBefore);
    root.appendChild(bar);
    root.appendChild(list);

    tracks.forEach(function (tr) {
        var row = el('div', 'sc-row');
        row.dataset.key = tr.key;
        row.style.setProperty('--sc-c', tr.color);

        var head = el('div', 'sc-head');
        head.appendChild(el('span', 'sc-dot'));
        var nm = el('span', 'sc-name'); nm.textContent = tr.label; nm.title = tr.label;
        head.appendChild(nm);
        // always in the DOM, hidden when it does not apply: setBuffers() can turn a
        // part empty (or fill it) and there would be no node to flip otherwise
        if (T.empty) {
            var badge = el('span', 'sc-empty'); badge.textContent = T.empty; badge.hidden = !tr.empty; head.appendChild(badge);
        }

        var tg = el('div', 'sc-tg');
        function mk(cls, short, full) {
            var b = el('button', 'sc-tgb ' + cls, short);
            b.type = 'button';
            b.setAttribute('aria-pressed', 'false');
            b.setAttribute('aria-label', full);
            b.title = full;
            return b;
        }
        var soloB = mk('sc-solo', T.solo || 'S', (T.solo_full || 'Solo') + ' — ' + tr.label);
        var muteB = mk('sc-mute', T.mute || 'M', (T.mute_full || 'Mute') + ' — ' + tr.label);
        tg.appendChild(soloB); tg.appendChild(muteB);

        var wrap = el('div', 'sc-wave');
        var cv = document.createElement('canvas');
        cv.className = 'sc-cv';
        wrap.appendChild(cv);
        wrap.appendChild(el('i', 'sc-ph'));

        var vol = document.createElement('input');
        vol.type = 'range'; vol.className = 'sc-vol';
        vol.min = '0'; vol.max = '100'; vol.value = '100';
        vol.setAttribute('aria-label', (T.volume_for || 'Volume') + ' — ' + tr.label);

        row.appendChild(head); row.appendChild(tg); row.appendChild(wrap); row.appendChild(vol);

        if (typeof opts.onDownload === 'function') {
            var dl = el('button', 'sc-dl', SVG_DL);
            dl.type = 'button';
            dl.setAttribute('aria-label', (T.download || 'Download') + ' — ' + tr.label);
            dl.title = (T.download || 'Download') + ' — ' + tr.label;
            dl.addEventListener('click', function () { opts.onDownload(tr); });
            row.appendChild(dl);
            tr.el.dl = dl;
        }

        list.appendChild(row);
        tr.el = { row: row, canvas: cv, wave: wrap, ph: wrap.querySelector('.sc-ph'), solo: soloB, mute: muteB, vol: vol, dl: tr.el.dl, badge: head.querySelector('.sc-empty') };

        soloB.addEventListener('click', function () {
            tr.solo = !tr.solo;
            soloB.classList.toggle('is-on', tr.solo);
            soloB.setAttribute('aria-pressed', String(tr.solo));
            applyGains();
        });
        muteB.addEventListener('click', function () {
            tr.mute = !tr.mute;
            muteB.classList.toggle('is-on', tr.mute);
            muteB.setAttribute('aria-pressed', String(tr.mute));
            applyGains();
        });
        vol.addEventListener('input', function () { tr.vol = (+vol.value) / 100; applyGains(); });
        wrap.addEventListener('click', function (e) {
            var r = wrap.getBoundingClientRect();
            var frac = Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width)));
            seekTo(frac * duration(), true);
        });
    });

    /* ── waveforms ────────────────────────────────────────────────────────── */
    var drawScheduled = 0;
    function draw() {
        tracks.forEach(function (tr) {
            var cv = tr.el.canvas;
            if (!cv || !cv.clientWidth) { return; }
            if (window.WaveformPreview && window.WaveformPreview.render) {
                if (!tr.wave) { tr.wave = waveSource(tr.buffer); }
                try { window.WaveformPreview.render(cv, tr.wave, { color: tr.color, silenceMinSec: 0.3 }); return; } catch (_) {}
            }
            // WaveformPreview is a @light script; if it never arrived the console
            // still works — the row just shows a flat baseline instead of peaks.
            var c2 = cv.getContext && cv.getContext('2d');
            if (c2) { cv.width = cv.clientWidth; cv.height = cv.clientHeight; c2.clearRect(0, 0, cv.width, cv.height); }
        });
    }
    function relayout() {
        clearTimeout(drawScheduled);
        drawScheduled = setTimeout(draw, 60);
    }
    // First paint has to wait for layout: the canvases are inside a container the
    // consumer only just un-hid, so clientWidth is 0 in this tick.
    requestAnimationFrame(function () { requestAnimationFrame(draw); });
    var onResize = function () { relayout(); };
    window.addEventListener('resize', onResize);

    /* ── gains ────────────────────────────────────────────────────────────── */
    function gainOf(tr) {
        var anySolo = tracks.some(function (x) { return x.solo; });
        var audible = anySolo ? tr.solo : !tr.mute;
        return audible ? tr.vol : 0;
    }
    function applyGains() {
        var anySolo = tracks.some(function (x) { return x.solo; });
        var ac = ctxOf();
        tracks.forEach(function (tr) {
            var v = gainOf(tr);
            tr.el.row.classList.toggle('is-off', v === 0);
            tr.el.mute.classList.toggle('is-dim', anySolo && !tr.solo && !tr.mute);
            if (tr.gain && ac) {
                // short ramp: a step on a running source clicks
                try { tr.gain.gain.setTargetAtTime(v, ac.currentTime, 0.01); } catch (_) { tr.gain.gain.value = v; }
            }
        });
    }
    function gains() {
        var out = {};
        tracks.forEach(function (tr) { out[tr.key] = gainOf(tr); });
        return out;
    }
    function isDefaultMix() {
        return !tracks.some(function (tr) { return tr.solo || tr.mute || tr.vol !== 1; });
    }
    // A preset is a mute pattern applied for the person: {key: true} rows go
    // silent, every other row comes back on, and any solo is released — a solo
    // left standing would override the very pattern that was asked for. The
    // buttons are repainted through the same state the clicks use, so the rows
    // stay the single source of what «Скачать микс» will render.
    function setMutes(map) {
        tracks.forEach(function (tr) {
            tr.solo = false; tr.mute = !!(map && map[tr.key]);
            tr.el.solo.classList.remove('is-on'); tr.el.solo.setAttribute('aria-pressed', 'false');
            tr.el.mute.classList.toggle('is-on', tr.mute); tr.el.mute.setAttribute('aria-pressed', String(tr.mute));
        });
        applyGains();
    }

    /* ── transport ────────────────────────────────────────────────────────── */
    function killNodes() {
        token++;
        tracks.forEach(function (tr) {
            if (tr.node) { try { tr.node.onended = null; tr.node.stop(); } catch (_) {} }
            tr.node = null; tr.gain = null;
        });
        cancelAnimationFrame(raf); raf = 0;
    }

    function position() {
        if (!playing) { return offset; }
        var ac = ctxOf();
        if (!ac) { return offset; }
        var d = duration();
        var t = offset + (ac.currentTime - startedAt);
        if (loop && d > 0) { t = t % d; }
        return Math.max(0, Math.min(t, d));
    }

    function paintHead(t) {
        var d = duration();
        var pct = d > 0 ? (t / d) * 100 : 0;
        for (var i = 0; i < tracks.length; i++) { tracks[i].el.ph.style.left = pct + '%'; }
        cur.textContent = fmt(t);
        if (document.activeElement !== seek) { seek.value = String(Math.round(pct * 10)); }
    }

    function tick() {
        if (!playing) { return; }
        paintHead(position());
        raf = requestAnimationFrame(tick);
    }

    function setPlayUi(on) {
        playing = on;
        playBtn.innerHTML = on ? SVG_PAUSE : SVG_PLAY;
        playBtn.setAttribute('aria-label', (on ? T.pause : T.play) || (on ? 'Pause' : 'Play'));
        root.classList.toggle('is-playing', on);
        if (typeof opts.onPlayState === 'function') { opts.onPlayState(on); }
    }

    function play(at) {
        var ac = ctxOf();
        if (!ac || destroyed) { return; }
        killNodes();
        var d = duration();
        var start = Math.max(0, Math.min(typeof at === 'number' ? at : position(), Math.max(0, d - 0.02)));
        // iOS: resume() rejects when it is not inside the gesture chain
        if (ac.state === 'suspended') {
            if (window.__safeResume) { window.__safeResume(ac); }
            else { try { ac.resume().catch(function () {}); } catch (_) {} }
        }
        var when = ac.currentTime + 0.03, my = token;
        tracks.forEach(function (tr) {
            var s = ac.createBufferSource();
            s.buffer = tr.buffer;
            s.loop = loop;
            var g = ac.createGain();
            g.gain.value = gainOf(tr);
            s.connect(g); g.connect(ac.destination);
            s.start(when, start);
            tr.node = s; tr.gain = g;
        });
        // one ended-watcher, on the longest track
        var lead = tracks.reduce(function (a, b) { return b.buffer.duration > a.buffer.duration ? b : a; }, tracks[0]);
        if (lead.node) {
            lead.node.onended = function () {
                if (my !== token || loop) { return; }
                killNodes(); setPlayUi(false); offset = 0; paintHead(0);
            };
        }
        startedAt = when; offset = start;
        setPlayUi(true);
        cancelAnimationFrame(raf); raf = requestAnimationFrame(tick);
    }

    function pause() {
        if (!playing) { return; }
        var p = position();
        killNodes();
        offset = p;
        setPlayUi(false);
        paintHead(p);
    }

    function stop() {
        killNodes(); offset = 0; setPlayUi(false); paintHead(0);
    }

    function seekTo(t, keepPlaying) {
        var d = duration();
        t = Math.max(0, Math.min(t, d));
        if (playing && keepPlaying !== false) { play(t); }
        else { offset = t; paintHead(t); }
    }

    playBtn.addEventListener('click', function () { if (playing) { pause(); } else { play(position()); } });
    loopBtn.addEventListener('click', function () {
        loop = !loop;
        loopBtn.classList.toggle('is-on', loop);
        loopBtn.setAttribute('aria-pressed', String(loop));
        tracks.forEach(function (tr) { if (tr.node) { try { tr.node.loop = loop; } catch (_) {} } });
    });
    seek.addEventListener('input', function () { seekTo(duration() * (+seek.value / 1000), false); });
    seek.addEventListener('change', function () { seekTo(duration() * (+seek.value / 1000), true); });

    /* ── variant swap: same console, other buffers ────────────────────────── */
    function setBuffers(map) {
        var was = playing, at = position();
        killNodes();
        tracks.forEach(function (tr) {
            if (map[tr.key]) { tr.buffer = map[tr.key]; tr.wave = null; tr.level = levelOf(tr.buffer); }
        });
        markEmpty(tracks);
        tracks.forEach(function (tr) { if (tr.el.badge) { tr.el.badge.hidden = !tr.empty; } });
        tot.textContent = fmt(duration());
        draw();
        if (was) { play(Math.min(at, duration())); } else { setPlayUi(false); paintHead(at); }
    }

    function destroy() {
        destroyed = true;
        killNodes();
        window.removeEventListener('resize', onResize);
        clearTimeout(drawScheduled);
        root.innerHTML = '';
        root.classList.remove('sc', 'sc--nodl', 'is-playing');
    }

    applyGains();
    paintHead(0);

    return {
        el: root,
        slot: slotBefore,          // consumer hangs the variant switch here
        tracks: tracks,
        play: play, pause: pause, stop: stop, seek: seekTo,
        position: position, duration: duration,
        playing: function () { return playing; },
        gains: gains, isDefaultMix: isDefaultMix, setMutes: setMutes,
        setBuffers: setBuffers,
        relayout: relayout,
        destroy: destroy
    };
}

window.StemConsole = { create: create, isEmpty: isEmpty, version: '1.3.0' };

})();
